import { typeof as _typeof, slicedToArray as _slicedToArray } from './_virtual/_rollupPluginBabelHelpers.esm.js';
export { default as mergeWith } from 'lodash.mergewith';

var EMPTY_OBJECT = {};
var DEFAULT_KEY = '__default';
function hasDefault(x) {
  return _typeof(x) === 'object' && x !== null && DEFAULT_KEY in x;
}
function omit(object, keys) {
  var result = {};
  Object.keys(object).forEach(function (key) {
    if (keys.includes(key)) return;
    result[key] = object[key];
  });
  return result;
}
function pick(object, keys) {
  var result = {};
  keys.forEach(function (key) {
    if (key in object) {
      result[key] = object[key];
    }
  });
  return result;
}
function split(object, keys) {
  var picked = {};
  var omitted = {};
  Object.keys(object).forEach(function (key) {
    if (keys.includes(key)) {
      picked[key] = object[key];
    } else {
      omitted[key] = object[key];
    }
  });
  return [picked, omitted];
}

/**
 * Safely get value from a deeply nested object using dot notation path.
 *
 * Designed for robust attribute extraction with default value support.
 * Handles complex nested structures including objects and arrays gracefully.
 *
 * Features:
 * - Supports dot notation paths (e.g., 'user.profile.name', 'items.0.id')
 * - Works with both objects and arrays seamlessly
 * - Preserves falsy values (0, false, "") - only breaks on null/undefined
 * - Handles malformed paths (empty segments, consecutive dots)
 * - Returns fallback for missing/invalid paths instead of throwing errors
 * - Supports __default key extraction
 *
 * @param obj - The object or array to traverse
 * @param path - Dot notation string path or numeric index
 * @param fallback - Optional fallback value returned when path doesn't exist
 * @returns The value at the specified path, fallback if not found, or __default value if present
 */
function get(obj, path, fallback) {
  if (obj === null) return fallback;
  if (typeof path !== 'string' && typeof path !== 'number') return fallback;
  var key = typeof path === 'string' ? path.split('.') : [path];
  var current = obj;
  for (var index = 0; index < key.length; index += 1) {
    // Only break on null or undefined, not other falsy values like 0, false, ""
    if (current == null) break;
    var nextKey = key[index];
    if (nextKey === undefined || nextKey === '') {
      // Skip empty segments from consecutive dots like "a..b"
      continue;
    }

    // Check if current supports property access (object or array)
    if (_typeof(current) === 'object') {
      current = current[nextKey];
    } else {
      current = undefined;
      break;
    }
  }
  if (current === undefined) return fallback;
  return hasDefault(current) ? current[DEFAULT_KEY] : current;
}
var memoize = function memoize(fn) {
  var cache = new WeakMap();
  var memoizedFn = function memoizedFn(obj, path, fallback) {
    if (typeof obj === 'undefined' || typeof obj === 'string') {
      return fn(obj, path, fallback);
    }
    if (!cache.has(obj)) {
      cache.set(obj, new Map());
    }
    var map = cache.get(obj);
    if (map.has(path)) {
      return map.get(path);
    }
    var value = fn(obj, path, fallback);
    map.set(path, value);
    return value;
  };
  return memoizedFn;
};

/**
 * Get value from a deeply nested object using a string path.
 * Memoizes the value.
 * @param obj - the object
 * @param path - the string path
 * @param def  - the fallback value
 */
var memoizedGet = memoize(get);

/**
 * Get value from deeply nested object, based on path
 * It returns the path value if not found in object
 *
 * @param path - the string path or value
 * @param scale - the string path or value
 */
function getWithDefault(path, scale) {
  return memoizedGet(scale, path, path);
}
/**
 * Returns the items of an object that meet the condition specified in a callback function.
 *
 * @param object the object to loop through
 * @param fn The filter function
 */
function objectFilter(object, fn) {
  var result = {};
  Object.keys(object).forEach(function (key) {
    var value = object[key];
    var shouldPass = fn(value, key, object);
    if (shouldPass) {
      result[key] = value;
    }
  });
  return result;
}
var filterUndefined = function filterUndefined(object) {
  return objectFilter(object, function (val) {
    return val !== null && val !== undefined;
  });
};
var objectKeys = function objectKeys(obj) {
  return Object.keys(obj);
};

/**
 * Object.entries polyfill for Nodev10 compatibility
 */
var fromEntries = function fromEntries(entries) {
  return entries.reduce(function (carry, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    carry[key] = value;
    return carry;
  }, {});
};

export { EMPTY_OBJECT, filterUndefined, fromEntries, get, getWithDefault, memoize, memoizedGet, objectFilter, objectKeys, omit, pick, split };
//# sourceMappingURL=object.esm.js.map
